import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Box, Typography, Button, CircularProgress, Paper } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import proposalsServices from '../services/proposalsServices';
import publicServices from '../services/publicServices';

const PrintReceipt = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProposal = async () => {
      try {
        const response = token
          ? await publicServices.getPublicProposal(id, token)
          : await proposalsServices.getProposalById(id);
        setProposal(response.data);
      } catch (err) {
        setError('Failed to load receipt data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchProposal();
  }, [id, token]);

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      if (token) {
        // Public access - open URL directly
        const pdfUrl = proposalsServices.getReceiptPdfUrl(id, token);
        window.open(pdfUrl, '_blank');
      } else {
        // Authenticated access - download via axios with auth header
        const response = await proposalsServices.downloadReceiptPdf(id);
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Receipt-${proposal?.receiptId || proposal?.publicId || id}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Failed to download PDF:', err);
      alert('Failed to download PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !proposal) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
        <Typography color="error">{error || 'Receipt not found'}</Typography>
      </Box>
    );
  }

  // Submitted (unconfirmed) payment info must never appear on client documents.
  const payments = (proposal.payments || []).filter((p) => p.status !== 'submitted');
  
  // Calculate total from payments array
  const paymentsTotal = payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  
  // For Service proposals - calculate from workDescriptions if no payments
  const serviceTotal = proposal.proposalType === 'Service'
    ? (proposal.workDescriptions || []).reduce((sum, w) => sum + (parseFloat(w.price) || 0), 0)
    : 0;
  
  // Use payments total if available, otherwise fall back to service total, jobPrice, or data.totalPay
  const totalPaid = paymentsTotal > 0 
    ? paymentsTotal 
    : (serviceTotal || parseFloat(proposal.jobPrice) || parseFloat(proposal.data?.totalPay) || 0);
  
  // Payment method - from payments array, or fallback to paymentNotes/additionalNotes
  const paymentMethodFromPayments = payments.map(p => p.methodName).filter(Boolean).join(', ');
  const paymentMethod = paymentMethodFromPayments 
    || proposal.paymentNotes 
    || proposal.additionalNotes?.payment?.comment 
    || null;
  
  const receiptId = proposal.receiptId || `RCPT-${proposal.publicId}`;
  const client = proposal.property?.clients?.[0]?.client;
  const clientName = client?.fullName || 'Customer';

  return (
    <>
      <style>
        {`
          @media print {
            .no-print { display: none !important; }
            body { 
              -webkit-print-color-adjust: exact !important; 
              print-color-adjust: exact !important;
              background: white !important;
            }
            .receipt-container {
              box-shadow: none !important;
              margin: 0 !important;
            }
            @page { 
              margin: 0.5in; 
              size: letter; 
            }
          }
          @media screen {
            body {
              background-color: #f5f5f5;
            }
          }
        `}
      </style>
      
      {/* Action Buttons - Fixed at top */}
      <Box className="no-print" sx={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        right: 0, 
        zIndex: 1000,
        backgroundColor: '#0d3c61',
        padding: 2,
        display: 'flex',
        justifyContent: 'center',
        gap: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
      }}>
        <Button
          variant="contained"
          startIcon={downloading ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
          onClick={handleDownloadPdf}
          disabled={downloading}
          sx={{ 
            backgroundColor: '#4caf50',
            color: '#fff',
            fontWeight: 'bold',
            '&:hover': { backgroundColor: '#388e3c' }
          }}
        >
          {downloading ? 'Downloading...' : 'Download PDF'}
        </Button>
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          sx={{ 
            backgroundColor: '#fff',
            color: '#0d3c61',
            '&:hover': { backgroundColor: '#e3f2fd' }
          }}
        >
          Print
        </Button>
      </Box>

      {/* Spacer for fixed header */}
      <Box className="no-print" sx={{ height: 80 }} />

      {/* Receipt Content */}
      <Box className="receipt-container" sx={{ 
        maxWidth: 650, 
        margin: '20px auto', 
        backgroundColor: '#fff',
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <Box sx={{ 
          borderBottom: '2px solid #0d3c61',
          padding: 4,
          textAlign: 'center',
        }}>
          <img src="/images/Airmaxx-Logo-Trans.png" alt="Airmaxx" style={{ maxHeight: 60, marginBottom: 8 }} />
          
          <Typography sx={{ color: '#333', fontSize: 14 }}>
            1865 John Towers Ave, Ste B, El Cajon, CA 92020
          </Typography>
        </Box>

        {/* PAID IN FULL Badge */}
        <Box sx={{ textAlign: 'center', py: 3, backgroundColor: '#fafafa' }}>
          <Box sx={{ 
            display: 'inline-block',
            backgroundColor: '#4caf50',
            color: '#fff',
            px: 4,
            py: 1.5,
            borderRadius: 1,
            fontWeight: 'bold',
            fontSize: 16
          }}>
            ☑ PAID IN FULL
          </Box>
        </Box>

        {/* Receipt Info + Customer Info */}
        <Box sx={{ display: 'flex', gap: 3, p: 4, flexWrap: 'wrap' }}>
          {/* Receipt Information */}
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="subtitle2" sx={{ 
              color: '#0d3c61', 
              fontWeight: 'bold', 
              textTransform: 'uppercase',
              borderBottom: '2px solid #0d3c61',
              pb: 1,
              mb: 2,
              fontSize: 14
            }}>
              Receipt Information
            </Typography>
            <Typography sx={{ fontSize: 13, color: '#666', mb: 1 }}>
              <strong>Receipt #:</strong> {receiptId}
            </Typography>
            <Typography sx={{ fontSize: 13, color: '#666', mb: 1 }}>
              <strong>Date Issued:</strong> {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </Typography>
            {payments.length > 0 && payments[0].date && (
              <Typography sx={{ fontSize: 13, color: '#666', mb: 1 }}>
                <strong>Payment Date:</strong> {new Date(payments[0].date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </Typography>
            )}
            {paymentMethod && (
              <Typography sx={{ fontSize: 13, color: '#666' }}>
                <strong>Payment Method:</strong> {paymentMethod}
              </Typography>
            )}
          </Box>

          {/* Customer Information */}
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="subtitle2" sx={{ 
              color: '#0d3c61', 
              fontWeight: 'bold', 
              textTransform: 'uppercase',
              borderBottom: '2px solid #0d3c61',
              pb: 1,
              mb: 2,
              fontSize: 14
            }}>
              Customer Information
            </Typography>
            <Typography sx={{ fontSize: 13, color: '#666', mb: 1, fontWeight: 'bold' }}>
              {clientName}
            </Typography>
            {proposal.property?.address && (
              <Typography sx={{ fontSize: 13, color: '#666', mb: 1 }}>
                {proposal.property.address}
              </Typography>
            )}
            {client?.phone && (
              <Typography sx={{ fontSize: 13, color: '#666' }}>
                {client.phone}
              </Typography>
            )}
            {client?.email && (
              <Typography sx={{ fontSize: 13, color: '#666' }}>
                {client.email}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Services Provided */}
        <Box sx={{ px: 4, pb: 4 }}>
          <Typography variant="subtitle2" sx={{ 
            color: '#0d3c61', 
            fontWeight: 'bold', 
            textTransform: 'uppercase',
            borderBottom: '2px solid #0d3c61',
            pb: 1,
            mb: 2,
            fontSize: 14
          }}>
            Services Provided
          </Typography>

          <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            {/* Header Row */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              backgroundColor: '#f5f5f5',
              borderBottom: '1px solid #e0e0e0',
              p: 1.5
            }}>
              <Typography sx={{ fontSize: 12, fontWeight: 'bold', color: '#333' }}>
                {proposal.proposalType === 'Service' ? 'Service Description' : 'Project Number'}
              </Typography>
            </Box>
            
            {/* For Service proposals - show work descriptions */}
            {proposal.proposalType === 'Service' && proposal.workDescriptions?.length > 0 ? (
              proposal.workDescriptions.map((work, index) => (
                <Box key={index} sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  p: 2,
                  borderBottom: '1px solid #e0e0e0'
                }}>
                  <Typography sx={{ fontSize: 14, color: '#333', flex: 1, pr: 2 }}>
                    {work.description}
                  </Typography>
                </Box>
              ))
            ) : (
              /* Default - show project number */
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                p: 2,
                borderBottom: '1px solid #e0e0e0'
              }}>
                <Typography sx={{ fontSize: 14, color: '#333' }}>
                  {proposal.publicId}
                </Typography>
              </Box>
            )}

            {/* Payment Details - only show if payments array has data */}
            {payments.length > 0 && payments.map((payment, index) => (
              <Box key={index} sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                p: 1.5,
                backgroundColor: '#fafafa',
                borderBottom: index < payments.length - 1 ? '1px solid #e0e0e0' : 'none'
              }}>
                <Typography sx={{ fontSize: 12, color: '#666' }}>
                  {payment.methodName || 'Payment'} {payment.reference ? `(Ref: ${payment.reference})` : ''}
                  {payment.date ? ` - ${new Date(payment.date).toLocaleDateString()}` : ''}
                </Typography>
                <Typography sx={{ fontSize: 12, color: '#666' }}>
                  ${parseFloat(payment.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </Typography>
              </Box>
            ))}

            {/* Total Row */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              backgroundColor: '#e8f5e9',
              p: 2
            }}>
              <Typography sx={{ fontSize: 14, fontWeight: 'bold', color: '#2e7d32' }}>
                Total Paid:
              </Typography>
              <Typography sx={{ fontSize: 18, fontWeight: 'bold', color: '#2e7d32' }}>
                ${totalPaid.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </Typography>
            </Box>
          </Paper>
        </Box>

        {/* Thank You Message */}
        <Box sx={{ textAlign: 'center', px: 4, pb: 4 }}>
          <Typography variant="h5" sx={{ color: '#0d3c61', fontWeight: 'bold', mb: 2 }}>
            Thank you for your business!
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#666', lineHeight: 1.6 }}>
            This receipt confirms payment in full for all services rendered.<br />
            Please keep for your records.
          </Typography>
        </Box>

        {/* Footer */}
        <Box sx={{ 
          backgroundColor: '#f8f9fa',
          p: 3,
          textAlign: 'center',
        }}>
          <Typography sx={{ fontSize: 12, color: '#666', mb: 1 }}>
            For questions or concerns, contact us at
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#0d3c61', fontWeight: 'bold' }}>
            619-655-3010 | support@airmaxx.com
          </Typography>
        </Box>
      </Box>

      {/* Spacer at bottom */}
      <Box className="no-print" sx={{ height: 40 }} />
    </>
  );
};

export default PrintReceipt;
